import React, { useEffect, useMemo, useState, useRef } from "react";
import Layout from "./layout/Layout";
import { call, getBaseUrl } from "../../common/jsonRpcRoutes";
import { decodeVouchersFromUrl } from "../utils/voucherUrlUtils";
import { t } from "i18next";
import PaymentTimeoutPage from "./PaymentTimeoutPage";
import PaymentErrorPage from "./PaymentErrorPage";
import PaymentConfirmationLayout from "./confirmation/PaymentConfirmationLayout";
import PaymentStatusMessage from "./confirmation/PaymentStatusMessage";
import RewardsBanner, { type RewardsBannerVariant } from "./shared/RewardsBanner";
import RewardsSignUpSection from "./shared/RewardsSignUpSection";
import RewardsSignInSection from "./shared/RewardsSignInSection";
import { isRewardsEligible } from "../utils/rewardsEligibility";
import { useConfirmationRewards } from "./hooks/useConfirmationRewards";
import type { LinkedBookingSummary } from "./confirmation/LinkedBookingsDetails";
import { isAuthenticationPayment } from "../utils/paymentHelpers";
import { getFeatureNameForAction } from "../utils/featureSentiment";
import { notifyBugsnag } from "../bugsnag";
import { getHidePricesByMenu } from "../utils/menuPriceHelpers";
import { useGuestCheckoutSettings } from "./context/GuestCheckoutSettingsContext";
import { formatTime, formatBookingDate } from "../../utils/countryLocaleMap";
import { useWidgetLocale } from "./hooks/useWidgetLocale";
import type { Booking, BookingAdditionalType, CustomerPreOrder, TaxBreakdownEntry } from "../../common/jsonRpcTypes";
import { buildBookingTypeGroups, type BookingTypeGroup } from "../utils/bookingTypeGroups";
import { isMfeEmbedded, retryWithinEmbeddedElement } from "../utils/mfeElement";
import analytics, { readPartnerAnalyticsParams } from "../analytics";
import ReturnUrlRedirect from "./shared/ReturnUrlRedirect";
import { returnRedirectFromCheckDeposit } from "../utils/returnUrlRedirect";
import type { ReturnRedirectData } from "../utils/returnUrlRedirect";
import { getSafeReturnUrl } from "./bookingForm/bookingUrlParams";
import "../styles/confirmation.scss";

// Configuration for payment confirmation polling
const INITIAL_POLL_INTERVAL_MS = 1000; // Start polling every 1 second
const MAX_POLL_INTERVAL_MS = 3000; // Maximum polling interval of 3 seconds
const POLL_INTERVAL_INCREASE_FACTOR = 1.5; // Increase interval by 50% each time
const POLLS_BEFORE_SLOWDOWN = 30; // Start slowing down after 30 polls (30 seconds)
const TIMEOUT_MS = 120000; // Extended timeout to 120 seconds (2 minutes)
// A deposit check can fail transiently (a gateway timeout, a dropped connection)
// while the payment itself is still being reconciled, so only surface an error
// once several consecutive attempts have failed.
const CONSECUTIVE_POLL_FAILURES_BEFORE_ERROR = 4;
/**
 * How long the deposit check made when the deadline is reached is given before
 * the deadline is reported anyway.
 *
 * No polling is scheduled past the deadline, and `call` sends its request with
 * no timeout or abort signal, so a request that never settles would otherwise
 * leave the guest on the waiting screen for good.
 */
const FINAL_CHECK_TIMEOUT_MS = 10000;

/**
 * The subset of {@link CustomerPreOrder} that the confirmation page reads. Both
 * `/loadClientBasket` (a full `CustomerPreOrder`) and the merged booking-type
 * baskets satisfy this shape, so it can be built without an unsafe cast.
 */
type ConfirmPreOrder = Pick<
  CustomerPreOrder,
  | "items"
  | "forNames"
  | "email"
  | "serviceChargePercentage"
  | "serviceChargeAmount"
  | "totalCost"
  | "totalCostWithService"
  | "requiredPaymentAmount"
  | "tax_inclusive"
  | "tax_label"
  | "tax_breakdown"
  | "total_net"
  | "total_gross"
  | "customer_preorder_settings"
>;

/**
 * A single booking-type basket as returned by `/getAllBookingTypeBaskets`. Only
 * the fields the merge reads are declared here.
 */
interface BookingTypeBasket {
  bookingType: string;
  bookingTypeName?: string;
  booking?: { type?: string; additional_types?: BookingAdditionalType[] };
  items?: CustomerPreOrder["items"];
  forNames?: string[];
  email?: string;
  serviceChargePercentage?: number;
  serviceChargeAmount?: number;
  totalCost?: number;
  totalCostWithService?: number;
  requiredPaymentAmount?: number;
  tax_inclusive?: boolean;
  tax_label?: string;
  tax_breakdown?: TaxBreakdownEntry[];
  total_net?: number;
  total_gross?: number;
}

/**
 * Aggregate the per-basket tax breakdown entries into a single breakdown, summing
 * amounts that share the same label so the confirmation shows one line per tax band.
 */
function mergeTaxBreakdowns(baskets: BookingTypeBasket[]): TaxBreakdownEntry[] {
  const totals = new Map<string, number>();
  for (const basket of baskets) {
    for (const entry of basket.tax_breakdown ?? []) {
      totals.set(entry.label, (totals.get(entry.label) ?? 0) + entry.amount);
    }
  }
  return Array.from(totals, ([label, amount]) => ({ label, amount }));
}

/**
 * Resolve a single basket's service charge. Prefer the stored value; otherwise
 * fall back to the net→gross difference — but only for tax-inclusive baskets. For
 * tax-exclusive baskets that difference is tax, not a service charge, so it must
 * not be counted here (it is surfaced via the tax breakdown instead).
 */
function basketServiceCharge(basket: BookingTypeBasket): number {
  if (basket.serviceChargeAmount !== undefined) return basket.serviceChargeAmount;
  const hasTax = basket.tax_inclusive === false && (basket.tax_breakdown?.length ?? 0) > 0;
  if (hasTax) return 0;
  const gross = basket.totalCostWithService ?? basket.totalCost ?? 0;
  return Math.max(0, gross - (basket.totalCost ?? 0));
}

/**
 * Combine every booking-type basket for a booking into a single pre-order shape.
 *
 * The /book journey stores its pre-order under the specific booking-type key
 * (not the "default" one `/loadClientBasket` reads), so on /confirm we fall back
 * to all baskets and merge them. Totals, the service charge and any tax breakdown
 * are summed from the stored basket values so the confirmation renders them
 * accurately (for tax-exclusive venues the net→gross difference is tax and must
 * not be shown as a service charge).
 */
function mergeBookingTypeBaskets(baskets: BookingTypeBasket[]): ConfirmPreOrder | null {
  if (!Array.isArray(baskets)) return null;
  const withItems = baskets.filter((basket) => (basket.items?.length ?? 0) > 0);
  if (withItems.length === 0) return null;

  const [base] = withItems;
  const sum = (pick: (basket: BookingTypeBasket) => number | undefined) =>
    withItems.reduce((total, basket) => total + (pick(basket) ?? 0), 0);

  const totalCostWithService = sum((basket) => basket.totalCostWithService ?? basket.totalCost);
  const serviceChargeAmount = withItems.reduce((total, basket) => total + basketServiceCharge(basket), 0);
  // The service charge amount is summed across every basket, so the accompanying
  // percentage is only meaningful when all contributing baskets share one rate.
  // If linked booking types carry different rates, omit the percentage so
  // BookingDetailsCard shows just the (correct) amount rather than a mismatched label.
  const contributingRates = new Set(
    withItems.filter((basket) => basketServiceCharge(basket) > 0).map((basket) => basket.serviceChargePercentage),
  );
  const serviceChargePercentage = contributingRates.size === 1 ? [...contributingRates][0] : undefined;
  const taxBreakdown = mergeTaxBreakdowns(withItems);
  // A booking with linked bookings can mix tax modes across booking-type baskets,
  // so derive the tax-exclusive flag from any basket (not just the first) and take
  // the tax label from whichever basket is actually tax-exclusive.
  const taxExclusiveBasket = withItems.find((basket) => basket.tax_inclusive === false);
  const hasTax = taxExclusiveBasket !== undefined && taxBreakdown.length > 0;

  const merged: ConfirmPreOrder = {
    ...base,
    items: withItems.flatMap((basket) => basket.items ?? []),
    serviceChargeAmount,
    serviceChargePercentage,
    totalCostWithService,
    // Summed across baskets so a linked booking where any type actually
    // requires pre-order payment is correctly treated as payable overall.
    requiredPaymentAmount: sum((basket) => basket.requiredPaymentAmount),
    ...(hasTax
      ? {
          tax_inclusive: false,
          tax_label: taxExclusiveBasket.tax_label ?? base.tax_label,
          tax_breakdown: taxBreakdown,
          total_net: sum((basket) => basket.total_net ?? basket.totalCostWithService ?? basket.totalCost),
          total_gross: sum((basket) => basket.total_gross ?? basket.totalCostWithService),
        }
      : {}),
  };
  return merged;
}

const loadPreOrderData = async (
  bookingId: string,
  action: string | null,
  booking: Booking | null,
  setPreOrderData: React.Dispatch<React.SetStateAction<ConfirmPreOrder | null>>,
  setHidePricesByMenu: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  setBookingTypeGroups: React.Dispatch<React.SetStateAction<BookingTypeGroup[]>>,
) => {
  try {
    const preOrder = await call("/loadClientBasket", { booking_id: bookingId });

    // The /book journey stores its basket under the specific booking-type key, so the
    // default /loadClientBasket read comes back empty. Fall back to every booking-type
    // basket so pre-order items still show on /confirm, as they do for the other flows.
    let resolvedPreOrder: ConfirmPreOrder | null = preOrder ?? null;
    if (action === "book" && !(preOrder?.items?.length ?? 0)) {
      try {
        const baskets = await call("/getAllBookingTypeBaskets", { booking_id: bookingId });
        const merged = mergeBookingTypeBaskets(baskets ?? []);
        if (merged) {
          resolvedPreOrder = merged;
        }
        // Keep the per-booking-type breakdown alongside the merged totals so a
        // linked booking shows what was ordered against each booking type.
        setBookingTypeGroups(
          buildBookingTypeGroups(baskets ?? [], {
            mainBookingTypeName: booking?.bookingTypeName,
            mainBookingTime: booking?.time,
          }),
        );
      } catch {
        // Ignore — fall back to the (possibly empty) client basket above.
      }
    }

    if (resolvedPreOrder) {
      setPreOrderData(resolvedPreOrder);
      const hidePricesMapping = getHidePricesByMenu(resolvedPreOrder.customer_preorder_settings ?? {});
      setHidePricesByMenu(hidePricesMapping);
    }

    await call("/clearPreorder", { booking_id: bookingId });
  } catch {
    console.log("No pre-order data available or failed to clear cache");
  }
};

interface PaymentPollingResult {
  status: boolean;
  error: string | null;
  isTimeout: boolean;
  paymentError: string | null;
  bookingData: Booking | null;
  /**
   * The payment is confirmed but its booking detail could not be read, so the
   * confirmation is shown without it. Set only when Collins reported the
   * deposit as paid and `/checkDeposit` still returned no booking.
   */
  confirmedWithoutDetails: boolean;
  preOrderData: ConfirmPreOrder | null;
  /** Per-booking-type breakdown, populated only for linked bookings. */
  bookingTypeGroups: BookingTypeGroup[];
  hidePricesByMenu: Record<string, boolean>;
  isBaseUrlReady: boolean;
  /** Resolves once the `purchase` conversion has been dispatched. */
  purchaseTracked: React.RefObject<Promise<void>>;
}

/** Parses Collins' formatted deposit total (e.g. "£20.00") into a number. */
function parseDepositTotal(depositTotal: string | null): number {
  if (!depositTotal) return 0;
  const amount = Number(depositTotal.replace(/[^0-9.]/g, ""));
  return Number.isFinite(amount) ? amount : 0;
}

/**
 * Reports the confirmed payment as a `purchase`.
 *
 * The service is configured from the URL because this page is reached via a
 * payment-processor redirect, which carries the tracking context forward. The
 * purchase itself is de-duplicated per booking inside the service, so arriving
 * here after the booking flow already reported it fires nothing.
 *
 * `paymentJourneyId` is the identifier the payment was initiated against — the
 * pending booking on the initial `/book` flow, the booking itself elsewhere — so
 * the conversion matches its `payment_initiated` even where the ids differ.
 */
function trackPaymentPurchase(booking: Booking, paymentJourneyId?: string): Promise<void> {
  analytics.configure({
    ...readPartnerAnalyticsParams(),
    venue: { id: booking.bookingId, title: booking.venue.title, currency: booking.venue.currency },
  });

  return analytics.purchase({
    id: booking.bookingId,
    journeyId: paymentJourneyId,
    typeName: booking.bookingTypeName,
    numPeople: booking.guestCount,
    value: parseDepositTotal(booking.depositTotal),
    currency: booking.venue.currency,
    status: booking.status,
  });
}

/** The shape `/checkDeposit` answers with. */
type CheckDepositResult = Awaited<ReturnType<typeof call<"/checkDeposit">>>;

const RETRY_ELEMENT_BY_ROUTE: Record<string, string> = {
  book: "booking-form",
  "secure-your-booking": "secure-your-booking",
  "manage-your-order": "manage-your-order",
  "place-your-order": "place-your-order",
};

/** Send the retry to the matching route on the configured Guest Checkout origin. */
function retryOnGuestCheckoutOrigin(route: string, urlParams: URLSearchParams): void {
  const retryUrl = new URL(`/${route}`, getBaseUrl() || window.location.origin);
  retryUrl.search = urlParams.toString();
  window.location.href = retryUrl.toString();
}

/**
 * Send a guest whose payment failed back to the step they paid from, so they
 * can try again, and report whether the page is being left.
 *
 * The journey's own route is preferred; anything else falls back to the booking
 * form rather than being trusted as a path. Embedded elements must not navigate
 * to that route on the partner origin: the route belongs to Guest Checkout, so
 * the retry either reloads the host page's originating element or goes to the
 * configured Guest Checkout origin.
 */
function redirectToRetryAfterFailedPayment(urlParams: URLSearchParams): boolean {
  const actionUrl = urlParams.get("action");

  if (urlParams.get("redirect_status") !== "failed" || !actionUrl) return false;

  const validRoutes = ["secure-your-booking", "manage-your-order", "place-your-order"];
  const redirectRoute = validRoutes.includes(actionUrl) ? actionUrl : "book";

  if (!isMfeEmbedded()) {
    window.location.href = `/${redirectRoute}?${urlParams.toString()}`;
    return true;
  }

  const retryElement = RETRY_ELEMENT_BY_ROUTE[redirectRoute];
  if (retryElement && document.querySelector(retryElement)) {
    retryWithinEmbeddedElement(redirectRoute, urlParams);
  } else {
    retryOnGuestCheckoutOrigin(redirectRoute, urlParams);
  }

  return true;
}

/** What the confirmation knew about a payment it could not confirm. */
interface UnconfirmedPaymentContext {
  bookingId: string | null;
  pendingBookingId: string | null;
  intent: string | null;
  action: string | null;
  polls: number;
  elapsedMs: number;
  lastPollFailed: boolean;
}

/**
 * Reports a payment the confirmation gave up on.
 *
 * This is the only place a confirmation is an error. A deposit Collins has not
 * confirmed yet is the normal state of the first seconds of a /book journey —
 * the booking is created by a Stripe webhook after the guest is redirected —
 * and reporting each of those polls made one guest's ordinary wait read as a
 * hundred faults. What matters is the outcome: the guest reached the deadline
 * with a payment taken and nothing to show them, and that is worth exactly one
 * event, raised here.
 *
 * Carries no card or personal detail: the identifiers, how long the page asked
 * for, and whether the asking itself was failing.
 */
function reportUnconfirmedPayment(context: UnconfirmedPaymentContext): void {
  notifyBugsnag(new Error("Payment confirmation timed out with no confirmed deposit"), {
    route: "/confirm",
    ...context,
  });
}

function usePaymentPolling(): PaymentPollingResult {
  const [status, setStatus] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isTimeout, setIsTimeout] = useState<boolean>(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [bookingData, setBookingData] = useState<Booking | null>(null);
  const [confirmedWithoutDetails, setConfirmedWithoutDetails] = useState<boolean>(false);
  const [preOrderData, setPreOrderData] = useState<ConfirmPreOrder | null>(null);
  const [bookingTypeGroups, setBookingTypeGroups] = useState<BookingTypeGroup[]>([]);
  const [hidePricesByMenu, setHidePricesByMenu] = useState<Record<string, boolean>>({});
  const [isBaseUrlReady, setIsBaseUrlReady] = useState<boolean>(false);
  const hasProcessedSuccessRef = useRef<boolean>(false);
  const purchaseTrackedRef = useRef<Promise<void>>(Promise.resolve());
  /**
   * The payment is confirmed, whether or not its booking detail has been read.
   * Read by the timeout, which must never tell a guest whose payment landed
   * that it is taking too long.
   */
  const hasConfirmedPaymentRef = useRef<boolean>(false);
  const pollIntervalRef = useRef<number>(INITIAL_POLL_INTERVAL_MS);
  const pollCountRef = useRef<number>(0);
  const hasTimedOutRef = useRef<boolean>(false);
  const consecutivePollFailuresRef = useRef<number>(0);

  // Check if base URL is ready (for MFE embedding)
  useEffect(() => {
    // Only the standalone MFE web components (e.g. <secure-your-booking>,
    // <booking-form>) call setBaseUrl. In the full <guest-checkout> app there
    // is no such element, so an empty base URL is the intended same-origin
    // value and we can proceed immediately.
    const isMfe = isMfeEmbedded();

    if (!isMfe || getBaseUrl()) {
      setIsBaseUrlReady(true);
      return;
    }

    const checkBaseUrl = () => {
      if (getBaseUrl()) {
        setIsBaseUrlReady(true);
      } else {
        // Check again after 100ms
        setTimeout(checkBaseUrl, 100);
      }
    };

    // Start checking after a small delay to allow the parent component to set the base URL
    setTimeout(checkBaseUrl, 50);
  }, []);

  useEffect(() => {
    if (!isBaseUrlReady) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const actionUrl = urlParams.get("action");

    if (redirectToRetryAfterFailedPayment(urlParams)) {
      return;
    }

    const bookingId = urlParams.get("booking_id");
    const pendingBookingId = urlParams.get("pending_booking_id");
    const effectiveBookingId = bookingId ?? pendingBookingId;
    const paymentIntentId = urlParams.get("payment_intent") ?? urlParams.get("setup_intent");

    if (!effectiveBookingId) {
      setError(t("missing_booking_id", "Missing booking ID"));
      return;
    }

    const storageKey = `payment_timeout_start_${effectiveBookingId}`;
    const timeoutStartTime = sessionStorage.getItem(storageKey) ?? Date.now().toString();
    sessionStorage.setItem(storageKey, timeoutStartTime);

    const elapsedTime = Date.now() - Number(timeoutStartTime);
    const remainingTime = Math.max(0, TIMEOUT_MS - elapsedTime);

    let intervalId: NodeJS.Timeout | undefined;
    let timeoutId: NodeJS.Timeout | undefined;
    let finalCheckId: NodeJS.Timeout | undefined;

    const handleSuccess = (booking: Booking) => {
      hasProcessedSuccessRef.current = true;
      setStatus(true);
      setBookingData(booking);
      // Supersedes any paid acknowledgement shown while the detail was pending.
      setConfirmedWithoutDetails(false);
      // A confirmed booking outranks a deadline already reported: an in-flight
      // check can settle after the deadline fires, and the timeout view would
      // otherwise hide the confirmation it arrived with.
      setIsTimeout(false);
      purchaseTrackedRef.current = trackPaymentPurchase(booking, pendingBookingId ?? effectiveBookingId);
      clearTimeout(timeoutId);
      clearTimeout(intervalId);
      sessionStorage.removeItem(storageKey);
      if (actionUrl !== "secure-your-booking") {
        loadPreOrderData(
          effectiveBookingId,
          actionUrl,
          booking,
          setPreOrderData,
          setHidePricesByMenu,
          setBookingTypeGroups,
        );
      }
    };

    /**
     * Acknowledge a payment Collins has confirmed whose booking detail could not
     * be read, without giving up on the detail.
     *
     * The guest is told the payment landed straight away — otherwise they sit on
     * the spinner until the 120s timeout and are told it is taking too long,
     * when it succeeded. Polling deliberately continues: a later response whose
     * booking does parse still reaches `handleSuccess`, which is the only path
     * that produces the reference, the purchase event and the pre-order detail.
     * The existing timeout bounds that recovery, and no longer surfaces as a
     * failure once the payment is acknowledged.
     */
    const acknowledgePaymentPendingDetails = () => {
      hasConfirmedPaymentRef.current = true;
      setConfirmedWithoutDetails(true);
    };

    const handlePaymentError = (errorMessage: string) => {
      hasProcessedSuccessRef.current = true;
      setPaymentError(errorMessage);
      clearTimeout(timeoutId);
      clearTimeout(intervalId);
      sessionStorage.removeItem(storageKey);
    };

    /**
     * Act on a deposit check's answer.
     *
     * Polls overlap — `scheduleNextPoll` starts the next check on a timer
     * without waiting for the previous one — so answers can arrive out of
     * order. Once the payment is confirmed, only a readable booking improves on
     * what the guest has already been told; an older response carrying a
     * payment error or a plain negative must not overturn it.
     */
    const applyDepositResult = (data: CheckDepositResult) => {
      consecutivePollFailuresRef.current = 0;
      setError(null);

      if (data.successful && data.booking) {
        handleSuccess(data.booking);
        return;
      }

      if (hasConfirmedPaymentRef.current) return;

      if (!data.successful && data.payment_error) {
        handlePaymentError(data.payment_error);
        return;
      }

      // Collins confirmed the deposit but the booking detail did not survive
      // parsing. Acknowledge the payment now and keep polling, so a later
      // readable response can still deliver the full confirmation.
      if (data.successful) {
        acknowledgePaymentPendingDetails();
        return;
      }

      setStatus(data.successful);
      setBookingData(data.booking);
    };

    /**
     * Ask Collins whether the deposit is paid and act on the answer.
     *
     * Unguarded, so the deadline can make one last check after polling has
     * stopped. Polling itself goes through {@link checkPaymentStatus}.
     */
    const evaluateDepositStatus = async () => {
      try {
        // Extract vouchers from URL parameters to restore after Stripe redirect
        const urlParams = new URLSearchParams(window.location.search);
        const encodedVouchers = urlParams.get("vouchers");
        const vouchers = decodeVouchersFromUrl(encodedVouchers ?? "");

        const data = await call("/checkDeposit", {
          ...(pendingBookingId && !bookingId
            ? { pending_booking_id: pendingBookingId }
            : { booking_id: effectiveBookingId }),
          intent: paymentIntentId ?? "",
          ...(vouchers.length > 0 && { vouchers }),
        });

        if (hasProcessedSuccessRef.current) return;

        applyDepositResult(data);
      } catch {
        // Once the payment is acknowledged the poll is only chasing display
        // detail, so a failed call is not something to alarm the guest with:
        // the error view outranks the acknowledgement and would replace "we
        // have your payment" with a configuration failure over a lost retry.
        if (hasConfirmedPaymentRef.current) return;

        consecutivePollFailuresRef.current++;
        if (consecutivePollFailuresRef.current >= CONSECUTIVE_POLL_FAILURES_BEFORE_ERROR) {
          setError(t("failed_load_payment_config", "Failed to load payment configuration."));
        }
      }
    };

    /** The polling entry point: skips the call once the wait has been settled. */
    const checkPaymentStatus = async () => {
      if (hasProcessedSuccessRef.current || hasTimedOutRef.current) return;
      await evaluateDepositStatus();
    };

    const scheduleNextPoll = () => {
      pollCountRef.current++;
      if (pollCountRef.current > POLLS_BEFORE_SLOWDOWN) {
        pollIntervalRef.current = Math.min(
          pollIntervalRef.current * POLL_INTERVAL_INCREASE_FACTOR,
          MAX_POLL_INTERVAL_MS,
        );
      }

      intervalId = setTimeout(() => {
        checkPaymentStatus();
        if (!hasProcessedSuccessRef.current && !hasTimedOutRef.current) {
          scheduleNextPoll();
        }
      }, pollIntervalRef.current);
    };

    /**
     * Settle the wait once the deadline is reached.
     *
     * Polling stops, then one last deposit check is made before the guest is
     * told the payment took too long. The answer can arrive between the final
     * poll and the deadline, earlier polls may have been failing, and on a
     * reload past a stored deadline no poll has run at all — so the deadline is
     * never reported on the strength of the clock alone.
     *
     * That check is bounded: `call` sends its request with no timeout or abort
     * signal, and this path schedules no further polling, so a request that
     * never settles would leave the guest on the waiting screen for good.
     *
     * A success, a payment error or a paid acknowledgement all settle the
     * question, and none of them may be recast as "taking too long".
     */
    const settleDeadline = () => {
      hasTimedOutRef.current = true;
      clearTimeout(intervalId);

      const reportTimeout = () => {
        sessionStorage.removeItem(storageKey);
        if (hasProcessedSuccessRef.current || hasConfirmedPaymentRef.current) return;
        reportUnconfirmedPayment({
          bookingId,
          pendingBookingId,
          intent: paymentIntentId,
          action: actionUrl,
          polls: pollCountRef.current,
          elapsedMs: Date.now() - Number(timeoutStartTime),
          lastPollFailed: consecutivePollFailuresRef.current > 0,
        });
        setIsTimeout(true);
      };

      finalCheckId = setTimeout(reportTimeout, FINAL_CHECK_TIMEOUT_MS);
      void evaluateDepositStatus().then(() => {
        clearTimeout(finalCheckId);
        reportTimeout();
      });
    };

    // A deadline already in the past (the guest reloaded, or the tab was
    // suspended past it) settles immediately — which still means asking Collins
    // once, not trusting a stale clock.
    if (remainingTime === 0) {
      settleDeadline();
    } else {
      checkPaymentStatus();
      scheduleNextPoll();
      timeoutId = setTimeout(settleDeadline, remainingTime);
    }

    return () => {
      clearTimeout(intervalId);
      clearTimeout(timeoutId);
      clearTimeout(finalCheckId);
    };
  }, [isBaseUrlReady]);

  return {
    status,
    error,
    isTimeout,
    paymentError,
    bookingData,
    confirmedWithoutDetails,
    preOrderData,
    bookingTypeGroups,
    hidePricesByMenu,
    isBaseUrlReady,
    purchaseTracked: purchaseTrackedRef,
  };
}

interface ConfirmPaymentSuccessViewProps {
  readonly bookingData: Booking;
  readonly preOrderData: ConfirmPreOrder | null;
  readonly bookingTypeGroups: BookingTypeGroup[];
  readonly hidePricesByMenu: Record<string, boolean>;
  readonly confirmationMode: "successWithDetails" | "successOnly";
  readonly featureName: string;
  readonly isBookingJourney: boolean;
  /** Shown in place of the rewards prompts when the guest is about to be sent to the partner's `return_url`. */
  readonly redirectNotice?: React.ReactElement | null;
}

/** Resolve the guest's name, preferring the details entered on the customer details form over the authenticated user. */
function resolveCustomerName(bookingData: Booking): string | null {
  const detailsName = [bookingData.bookingDetails?.first_name, bookingData.bookingDetails?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (detailsName) return detailsName;

  const userName = bookingData.user?.user_name?.trim();
  if (userName) return userName;
  return null;
}

function ConfirmPaymentSuccessView({
  bookingData,
  preOrderData,
  bookingTypeGroups,
  hidePricesByMenu,
  confirmationMode,
  featureName,
  isBookingJourney,
  redirectNotice,
}: ConfirmPaymentSuccessViewProps) {
  const countryCode = bookingData.venue.country_code ?? "GB";
  const timeLocale = useWidgetLocale(bookingData.venue.country_code);
  const canFormatTime = Boolean(bookingData.time && bookingData.date && typeof bookingData.date === "string");
  const formattedDateForConfirmation =
    bookingData.date && typeof bookingData.date === "string"
      ? formatBookingDate(bookingData.date, countryCode, "short", bookingData.venue.timezone)
      : "";
  const confirmationTime =
    canFormatTime && typeof bookingData.time === "string" && typeof bookingData.date === "string"
      ? formatTime(bookingData.time, bookingData.date, countryCode, timeLocale)
      : bookingData.time;

  const customerName = resolveCustomerName(bookingData);

  // Linked bookings are separate bookings with their own references, so surface
  // them all rather than only the one the payment was checked against.
  const additionalBookings: LinkedBookingSummary[] = (bookingData.additionalBookings ?? []).map((linked) => ({
    id: linked.bookingId,
    reference: linked.reference,
    typeName: linked.typeName,
    date: linked.date,
    time: linked.time,
    partySize: linked.numPeople ?? undefined,
  }));

  const preOrderItems = (preOrderData?.items ?? []).map((item) => {
    const forNameIndex = item.for_name_index ?? 0;
    const forNames = preOrderData?.forNames;
    const forName = Array.isArray(forNames) && forNames.length > forNameIndex ? forNames[forNameIndex] : undefined;
    return { ...item, for_name: forName };
  });

  const isAuthentication = isAuthenticationPayment(bookingData.depositTypePayment);
  const fullPaymentTotal = Number(bookingData.depositTotal ?? 0);
  const preorderTotal =
    preOrderData?.total_gross ??
    preOrderData?.totalCostWithService ??
    (preOrderData?.totalCost ?? 0) + (preOrderData?.serviceChargeAmount ?? 0);
  const hasPreorder = preOrderItems.length > 0;
  // Pre-order items existing doesn't mean they were paid for — the booking type
  // may require no payment (pay-at-venue). Only treat the pre-order as paid when
  // it actually has a required payment amount, so a pay-at-venue pre-order isn't
  // mistaken for one that was charged alongside the deposit.
  const preorderPaymentTaken = hasPreorder && (preOrderData?.requiredPaymentAmount ?? 0) > 0;

  // The deposit listed on the booking details card is the amount Collins recorded
  // as charged, carried through by `/checkDeposit`. It is never derived from the
  // totals here: a booking type with several menus can charge for only some of
  // them, so the difference between the payment and the pre-order total is not
  // the deposit. Absent means no deposit was taken (or it was an authentication
  // hold). Unless the venue bundles the deposit with the pre-order payment
  // (`takeWithPreorder`), paying for a pre-order is taken *instead of* the
  // deposit — no deposit money changes hands, so it must not be listed either.
  const depositAmount =
    preorderPaymentTaken && !bookingData.depositTakenWithPreorder ? undefined : bookingData.depositPaidAmount;

  // Amount to show as "paid" in the success message. Authentication holds take no
  // money for the deposit, so when the guest also paid for their pre-order the
  // amount they actually paid is the pre-order total — not the authentication
  // value. A pay-at-venue pre-order was never charged, so the authentication
  // (deposit) value is the correct amount to show instead.
  const amountPaid = isAuthentication && preorderPaymentTaken ? preorderTotal : fullPaymentTotal;

  return (
    <div className="app-main">
      <PaymentConfirmationLayout
        venueName={bookingData.venue.title}
        bookingRef={bookingData.bookingRef}
        depositTotal={amountPaid}
        isAuthentication={isAuthentication}
        preorderPaymentTaken={preorderPaymentTaken}
        partySize={bookingData.guestCount}
        date={formattedDateForConfirmation}
        time={confirmationTime}
        location={bookingData.venue.title}
        preOrderItems={preOrderItems}
        bookingTypeGroups={bookingTypeGroups}
        currency={bookingData.venue.currency}
        countryCode={bookingData.venue.country_code}
        email={preOrderData?.email}
        mode={confirmationMode}
        hidePricesByMenu={hidePricesByMenu}
        showBookingDetails
        bookingJourney={isBookingJourney}
        featureName={featureName}
        serviceChargeAmount={preOrderData?.serviceChargeAmount}
        serviceChargePercentage={preOrderData?.serviceChargePercentage}
        totalCostWithService={preOrderData?.totalCostWithService}
        taxInclusive={preOrderData?.tax_inclusive}
        taxBreakdown={preOrderData?.tax_breakdown}
        totalNet={preOrderData?.total_net}
        totalGross={preOrderData?.total_gross}
        taxLabel={preOrderData?.tax_label}
        userId={bookingData.user?.user_id ?? null}
        customerName={customerName}
        bookingType={bookingData.bookingTypeName}
        selectedOffer={bookingData.selectedOffer}
        depositAmount={depositAmount}
        depositTerms={bookingData.depositTerms}
        additionalBookings={additionalBookings}
        notice={redirectNotice}
      />
      {redirectNotice ? null : <ConfirmRewards bookingData={bookingData} />}
    </div>
  );
}

/**
 * Rewards banner and account-creation prompt for the /confirm page.
 *
 * A guest who paid a deposit reaches the confirmation through the payment
 * redirect rather than the in-page booking confirmation, so without this they
 * are never offered the account that Collins needs in order to attach their
 * points — `BookingRewardTransaction::getUserForTransaction()` resolves the
 * user from the booking's email, and no user means no reward.
 */
function ConfirmRewards({ bookingData }: { readonly bookingData: Booking }): React.ReactElement | null {
  const [signUpBalance, setSignUpBalance] = useState<number | undefined>(undefined);
  const [signedUp, setSignedUp] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [signedInBalance, setSignedInBalance] = useState<number | undefined>(undefined);

  const { rewards, source, affiliateId, bookingTypeName, bookingDetails, newsletterSignup } = bookingData;
  const email = bookingDetails?.email ?? undefined;
  const eligible = !!rewards && isRewardsEligible({ rewards, source, affiliateId, bookingTypeName });
  const { rewardsUrl, hasAccount, canSignUp } = useConfirmationRewards({ email, enabled: eligible });

  if (!rewards || !eligible) return null;

  const variant: RewardsBannerVariant = (() => {
    if (signedUp) return "justSignedUp";
    // Only claim the guest is signed in once we positively know an account
    // exists. Anything else — still checking, the lookup failed, or no email
    // was recovered — falls back to the sign-up copy, which is true either way.
    return hasAccount === true ? "hasAccount" : "signedOut";
  })();

  return (
    <>
      <RewardsBanner
        variant={variant}
        rewards={rewards}
        currency={bookingData.venue.currency}
        countryCode={bookingData.venue.country_code ?? "GB"}
        pointsBalance={signUpBalance ?? signedInBalance}
        rewardsUrl={rewardsUrl}
      />
      {canSignUp && !signedUp && email && (
        <RewardsSignUpSection
          email={email}
          firstName={bookingDetails?.first_name ?? ""}
          lastName={bookingDetails?.last_name ?? ""}
          newsletterSignup={newsletterSignup}
          onSignUpSuccess={(pointsBalance) => {
            setSignedUp(true);
            setSignUpBalance(pointsBalance);
          }}
        />
      )}
      {/* An existing account already earns the points; signing in is what shows
          the balance and opens the rewards link authenticated. */}
      {hasAccount === true && !signedIn && email && (
        <RewardsSignInSection
          email={email}
          onSignInSuccess={(pointsBalance) => {
            setSignedIn(true);
            setSignedInBalance(pointsBalance);
          }}
        />
      )}
    </>
  );
}

/**
 * Whether the guest returned from a card authentication rather than a charge.
 *
 * Collins derives the booking's own `depositTypePayment` from exactly this —
 * whether the intent id starts with `pi_` — so a confirmation that has no
 * booking to read the flag from can still tell the two apart. An id that cannot
 * be recognised is treated as an authentication, so a guest is never told money
 * was taken when it may only have been held.
 */
function isAuthenticationOnlyIntent(search: string): boolean {
  const params = new URLSearchParams(search);
  const intent = params.get("payment_intent") ?? params.get("setup_intent");
  return !intent?.startsWith("pi_");
}

/**
 * Acknowledges a payment Collins has confirmed whose booking detail could not be
 * read, while the poll keeps trying for that detail. It says only what the
 * intent supports: money taken for a charge, a hold for an authentication.
 */
function PaymentAcknowledgedView({ authenticationOnly }: { readonly authenticationOnly: boolean }) {
  return (
    <Layout
      showHeader
      main={
        authenticationOnly ? (
          <PaymentStatusMessage
            title={t("card_authenticated_title", "Card Authenticated")}
            message={t(
              "card_authenticated_without_details",
              "Your card has been authenticated for your booking, but no money has been taken at this time.",
            )}
          />
        ) : (
          <PaymentStatusMessage
            title={t("payment_received_title", "Payment Received")}
            message={t("payment_received_without_details", "We have received your payment for your booking.")}
          />
        )
      }
      aside={null}
    />
  );
}

interface ConfirmReturnRedirect extends ReturnRedirectData {
  returnUrl: string;
  returnMethod?: string;
}

/**
 * The partner `return_url` a paid `/book` journey should be sent on to.
 *
 * Legacy DMN `/book` sent every completed booking to `return_url`, paid or not.
 * A Stripe payment redirects the guest here instead of back to the booking
 * form, so this page picks the redirect up — once the booking itself has been
 * read, since the partner is told its reference.
 *
 * The target is the one the server bound to the pending booking before
 * payment, never this page's query string: anyone holding the link could edit
 * that, and a POST carries the guest's name and email.
 */
function useConfirmReturnRedirect(
  bookingData: Booking | null,
  confirmed: boolean,
  enabled: boolean,
): ConfirmReturnRedirect | null {
  return useMemo(() => {
    if (!enabled || !confirmed || !bookingData) return null;
    if (new URLSearchParams(globalThis.location.search).get("action") !== "book") return null;

    // KNOWN RISK: `http:` is accepted for POST as well as GET, matching legacy
    // DMN /book, so a POST callback's guest name and email can travel without
    // transport encryption. Requiring `https:` for POST would close this but
    // stop redirecting partners whose POST endpoint is plain http. The
    // in-page confirmation (BookingConfirmation) shares the same behaviour.
    const returnUrl = getSafeReturnUrl(bookingData.returnUrl);
    if (!returnUrl) return null;

    return {
      returnUrl,
      returnMethod: bookingData.returnMethod,
      ...returnRedirectFromCheckDeposit(bookingData),
    };
  }, [bookingData, confirmed, enabled]);
}

interface ConfirmPaymentProps {
  /**
   * Whether a paid `/book` journey is sent on to the partner's `return_url`.
   * Only the standalone `/confirm` page does this: an embedded element shows
   * its confirmation in place.
   */
  readonly allowReturnRedirect?: boolean;
}

const ConfirmPayment: React.FC<ConfirmPaymentProps> = ({ allowReturnRedirect = false }) => {
  const { isWidget } = useGuestCheckoutSettings();
  const {
    status,
    error,
    isTimeout,
    paymentError,
    bookingData,
    confirmedWithoutDetails,
    preOrderData,
    bookingTypeGroups,
    hidePricesByMenu,
    isBaseUrlReady,
    purchaseTracked,
  } = usePaymentPolling();
  const returnRedirect = useConfirmReturnRedirect(bookingData, status, allowReturnRedirect);

  const actionParam = new URLSearchParams(window.location.search).get("action") ?? undefined;
  const hasPreOrderItems = (preOrderData?.items?.length ?? 0) > 0;
  const confirmationMode: "successWithDetails" | "successOnly" = hasPreOrderItems
    ? "successWithDetails"
    : "successOnly";
  const featureName = getFeatureNameForAction(actionParam);

  // Show loading while waiting for base URL to be ready (MFE embedding scenario)
  if (!isBaseUrlReady) {
    return (
      <Layout
        showHeader
        main={
          <PaymentStatusMessage
            title={t("waiting_payment_confirmation", "Waiting for payment confirmation...")}
            message={t("please_wait_confirm_payment", "Please wait while we confirm your payment...")}
            showSpinner
          />
        }
        aside={null}
      />
    );
  }

  if (error) {
    return (
      <Layout
        showHeader
        main={
          <PaymentStatusMessage
            title={t("error", "Error")}
            message={error}
            tone="error"
            box={isWidget ? "error" : undefined}
          />
        }
        aside={null}
      />
    );
  }

  if (paymentError) {
    return <PaymentErrorPage paymentError={paymentError} />;
  }

  // The payment is confirmed but its booking detail is not readable yet, so the
  // guest gets that fact now while the poll keeps chasing the detail. Checked
  // before the timeout, which cannot fire as a failure once this is set.
  if (confirmedWithoutDetails) {
    return <PaymentAcknowledgedView authenticationOnly={isAuthenticationOnlyIntent(window.location.search)} />;
  }

  if (isTimeout) {
    return <PaymentTimeoutPage />;
  }

  const showConfirmation = status && bookingData;

  return (
    <Layout
      showHeader
      mainClassName="no-aside"
      main={
        showConfirmation ? (
          <ConfirmPaymentSuccessView
            bookingData={bookingData}
            preOrderData={preOrderData}
            bookingTypeGroups={bookingTypeGroups}
            hidePricesByMenu={hidePricesByMenu}
            confirmationMode={confirmationMode}
            featureName={featureName}
            isBookingJourney={actionParam === "book"}
            redirectNotice={
              returnRedirect && (
                <ReturnUrlRedirect
                  returnUrl={returnRedirect.returnUrl}
                  returnMethod={returnRedirect.returnMethod}
                  payload={returnRedirect.payload}
                  postBooking={returnRedirect.postBooking}
                  purchaseTracked={purchaseTracked}
                />
              )
            }
          />
        ) : (
          <PaymentStatusMessage
            title={t("waiting_payment_confirmation", "Waiting for payment confirmation...")}
            message={t("please_wait_confirm_payment", "Please wait while we confirm your payment...")}
            showSpinner
          />
        )
      }
      aside={null}
    />
  );
};

export default ConfirmPayment;
